import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Params, Router, ActivatedRoute } from "@angular/router";
import { CHttp } from "../../core/services/chttp.service";
import { config } from "../../app.config";
import { AuthDataStorageService } from "../../auth/services/auth-data-storage.service";
import { MemberService } from "../services/member.service";
import { parseMemberEmails } from '../member-emails';
import { MemberEmailSettingsComponent } from '../member-email-settings/member-email-settings.component';
import { BaseEditComponent } from "../../core/common/base-component";
import swal from "sweetalert2";
import { FormUtil } from "../../core/form.util";
import { MemberEvent } from "../members.interfaces";
import * as moment from "moment";
import { ReferralsService } from "../../referrals/referrals.service";
import {
  Referral,
  MemberReferrals,
  RewardInstance,
  RewardProgress,
} from "../../referrals/referrals.interfaces";
import {
  JobTitle,
  ManagementLevel,
  AreaOfBusiness,
  Employer,
} from "../members.interfaces";
import { environment } from "../../../environments/environment";
import { Title } from "@angular/platform-browser";
const alertify = require("alertify.js");

@Component({
  selector: "ch-member-edit",
  templateUrl: "./member-edit.component.html",
  styleUrls: ["./member-edit.component.css"],
})
export class MemberEditComponent extends BaseEditComponent implements OnInit, OnDestroy {
  /** Block profile saves and resets while the primary email is being refreshed. */
  public emailUnavailable = true;
  /** Email drafts participate in the existing Save action. */
  @ViewChild(MemberEmailSettingsComponent) public emailSettings: MemberEmailSettingsComponent;
  public memberId: string;
  public memberData: any = {};
  public name: string;
  public events: MemberEvent[];
  public userName: string;
  public approving: boolean;
  public showWebProfileModal: boolean;
  public showAbTestsModal: boolean;
  public showDevicesModal: boolean;
  public showSubscriptionModal: boolean;
  public showCancelMemberModal = false;
  public showNewIdModal = false;
  public showInvalidPlanDialog = false;
  public showMemberDeleteModal = false;
  public currentNotes: string;
  private initialData: any;
  public referrals: MemberReferrals = {
    pendingReferrals: 0,
    successfulReferrals: [],
    rewardProgress: [],
  };
  public rewardInstances: RewardInstance[] = [];
  public jobTitles: JobTitle[] = [];
  public managementLevels: ManagementLevel[] = [];
  public areasOfBusiness: AreaOfBusiness[] = [];
  public employers: Employer[] = [];
  public customEmployer = '';
  public employerId: string | null = null;
  public showCustomEmployer = false;
  private readonly OTHER_OPTION = '__OTHER__';
  private employersLoaded = false;
  public memberDataLoaded = false;
  public memberLoadFailed = false;
  public selectedJobTitleId: string | null = null;
  public selectedManagementLevelId: string | null = null;
  public selectedAreaOfBusinessId: string | null = null;

  constructor(
    private http: CHttp,
    private authDataStorageService: AuthDataStorageService,
    private memberService: MemberService,
    private referralsService: ReferralsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private titleService: Title,
  ) {
    super();
  }

  ngOnInit() {
    alertify.logPosition("bottom right");

    this.loadData();

    this.getReferrals();
    this.getRewardInstances();
    this.getJobTitles();
    this.getManagementLevels();
    this.getAreasOfBusiness();
    this.getEmployers();
  }

  ngOnDestroy() {
    this.titleService.setTitle(`CODE Hospitality CMS`);
  }

  loadData() {
    this.activatedRoute.queryParamMap
      .map((params: Params) => params.params)
      .subscribe((params) => {
        this.memberId = params.id;

        this.loading = true;
        this.memberDataLoaded = false;
        this.memberLoadFailed = false;
        this.memberService.getMember(this.memberId).map(response => {
          const member = response.json();
          const emails = parseMemberEmails(member && member.emailAddresses);
          if (emails.ok === false) { throw new Error('Invalid member details'); }
          return member;
        }).subscribe(
          (data) => {
            this.loading = false;
            this.loaded = true;
            this.memberData = data;
            this.titleService.setTitle(`${this.memberData.firstName} ${this.memberData.lastName} | Members | CODE Hospitality CMS`);
            this.initialData = { ...this.memberData };

            this.name =
              this.memberData.firstName + " " + this.memberData.lastName;
            if (
              "PROMO_CODE" === this.memberData.acquisitionChannel ||
              "DISCOUNT" === this.memberData.acquisitionChannel
            ) {
              this.memberData.vipYes = true;
            } else if (
              "STAFF_INCENTIVE" === this.memberData.acquisitionChannel
            ) {
              this.memberData.vipNo = true;
            }

            if (this.memberData.proofResourceId) {
              this.memberData.proofResourceUrl =
                config.api_url + "resources/" + this.memberData.proofResourceId;

              this.http
                .get(this.memberData.proofResourceUrl)
                .toPromise()
                .then((response) => {
                  this.memberData.proofResourceIsImage = response.headers
                    .get("content-type")
                    .startsWith("image");
                });
            }
            this.getEventLogEntries();
            this.memberDataLoaded = true;
            this.selectedJobTitleId = this.memberData.jobTitleId || null;
            this.selectedManagementLevelId = this.memberData.managementLevelId || null;
            this.selectedAreaOfBusinessId = this.memberData.areaOfBusinessId || null;
            this.tryCheckAndSetCustomFields();
          },
          (error) => {
            this.loading = false;
            this.loaded = true;
            this.memberLoadFailed = true;
            this.memberDataLoaded = false;
            this.showEmailRequestError('Could not load member details. Reload the page to try again.');
          },
        );
      });
  }

  private tryCheckAndSetCustomFields() {
    if (this.memberDataLoaded && this.employersLoaded) {
      this.checkAndSetCustomFields();
    }
  }

  private getReferrals() {
    this.loading = true;
    this.referralsService.getReferralsForMemberSuccess(this.memberId).subscribe(
      (resp) => {
        this.referrals.successfulReferrals = resp.json();
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
    this.referralsService.getReferralsForMemberProgress(this.memberId).subscribe(
      (resp) => {
        const data = resp.json();
        this.referrals.pendingReferrals = data.pendingReferralsCount;
        this.referrals.rewardProgress = data.rewardProgress;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getRewardInstances() {
    this.loading = true;
    this.referralsService
      .getReferralRewardInstancesForMember(this.memberId)
      .subscribe(
      (resp) => {
        this.rewardInstances = resp.json();
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getJobTitles() {
    this.memberService.getJobTitles().subscribe(
      (data) => {
        this.jobTitles = data;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getManagementLevels() {
    this.memberService.getManagementLevels().subscribe(
      (data) => {
        this.managementLevels = data;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getAreasOfBusiness() {
    this.memberService.getAreasOfBusiness().subscribe(
      (data) => {
        this.areasOfBusiness = data;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getEmployers() {
    this.memberService.getEmployers().subscribe(
      (data) => {
        this.employers = data;
        this.employersLoaded = true;
        this.tryCheckAndSetCustomFields();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private checkAndSetCustomFields() {
    // Employer - check if member has employerId (selected from list) or custom employer (no employerId)
    if (this.memberData.employerId) {
      // Member selected from list - find and set the employer by ID
      const matchingEmployer = this.employers.find(emp => emp.id === this.memberData.employerId);
      if (matchingEmployer) {
        this.employerId = matchingEmployer.id;
        this.memberData.employer = matchingEmployer.name;
        this.showCustomEmployer = false;
      }
    } else if (this.memberData.employer) {
      // No employerId means custom employer - show the "Other" option with custom text
      this.customEmployer = this.memberData.employer;
      this.memberData.employer = this.OTHER_OPTION;
      this.showCustomEmployer = true;
      this.employerId = null;
    }
  }

  public onEmployerChange() {
    this.showCustomEmployer = this.memberData.employer === this.OTHER_OPTION;
    if (!this.showCustomEmployer) {
      this.customEmployer = '';
      // Look up and store employer ID when selecting from dropdown
      const selectedEmployer = this.employers.find(emp => emp.name === this.memberData.employer);
      this.employerId = selectedEmployer ? selectedEmployer.id : null;
    } else {
      // No ID for custom employer
      this.employerId = null;
    }
  }

  private getEventLogEntries() {
    this.memberService.getMemberEvents(this.memberId).subscribe(
      (resp) => {
        this.events = resp
          .json()
          .map((event): MemberEvent => {
            return {
              ...event,
              createdAt: moment(event.createdAt),
              parsedDetails: this.parseDatetime(event.details),
            };
          })
          .sort(
          (a: MemberEvent, b: MemberEvent) => -a.createdAt.diff(b.createdAt),
        );
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  /** Show email transport failures using the existing page error toast. */
  public showEmailRequestError(message: string): void {
    alertify.error(message);
  }

  public async edit(form) {
    if (!this.memberDataLoaded || this.loading || this.emailUnavailable || !FormUtil.isValid(form)) {
      return;
    }

    this.loading = true;
    if (!this.emailSettings || !(await this.emailSettings.save())) {
      this.loading = false;
      return;
    }
    this.memberData.notes = this.currentNotes;
    const reqData = this.buildReqData(this.memberData);

    this.memberService.updateMember(reqData, this.memberId).subscribe(
      (resp) => {
        this.loading = false;
        this.cancel();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private buildReqData(data: any) {
    const reqData = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth || undefined,
      employer: data.employer === this.OTHER_OPTION ? this.customEmployer : data.employer,
      employerId: this.employerId,
      jobTitleId: this.selectedJobTitleId,
      managementLevelId: this.selectedManagementLevelId,
      areaOfBusinessId: this.selectedAreaOfBusinessId,
      notes: data.notes,
      acquisitionChannel: data.acquisitionChannel,
      country: data.country,
      city: data.city,
      zipCode: data.zipCode,
      referralsEnabledFlag: !!data.referralsEnabledFlag,
      updateNewRegistrationFieldsEnabledFlag: !!data.updateNewRegistrationFieldsEnabledFlag,
    };
    return reqData;
  }

  public cancel() {
    this.router.navigateByUrl("/admin/members");
  }

  public requestNewId(type: string) {
    this.pageLoading = true;
    this.memberService.requestNewId(this.memberId, type).subscribe(
      (resp) => {
        swal("Congratulations", "New id request successfully sent.", "success");
        this.renewStatus();
        this.pageLoading = false;
        this.loadData();
      },
      (error) => {
        this.pageLoading = false;
        this.alertError(error);
      },
    );
  }

  public approve() {
    if (this.approving) {
      return;
    }
    this.approving = true;
    this.loading = true;
    this.memberService.acceptMember(this.memberId).subscribe(
      (resp) => {
        this.renewStatus();
        this.memberData.lastStatusUpdate = new Date();
        this.approving = false;
        this.loadData();
      },
      (error) => {
        this.approving = false;
        this.loading = false;
        if (
          error.status == 400 &&
          error.error.startsWith("Couldn't find product plan")
        ) {
          this.showInvalidPlanDialog = true;
        } else {
          this.alertError(error);
        }
      },
    );
  }

  public downloadProof() {
    const a = document.createElement("a");
    a.href =
      this.memberData.proofResourceUrl +
      `?x-auth-token=${this.authDataStorageService.getSessionTokenFromStorage()}`;
    a.download = this.memberData.proofResourceUrl.split("/").pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public reject() {
    this.memberService.rejectMember(this.memberId).subscribe(
      (resp) => {
        this.renewStatus();
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public resetPassword(isDirtyEmail) {
    if (isDirtyEmail) {
      return;
    }

    this.pageLoading = true;
    this.memberService.forgottenPassword(this.memberData.email).subscribe(
      (resp) => {
        swal(
          "Congratulations",
          "Reset password request is successfully sent.",
          "success",
        );
        this.pageLoading = false;
        this.loadData();
      },
      (error) => {
        this.pageLoading = false;
      },
    );
  }

  public suspend() {
    const methodName = this.isSuspended ? "unsuspendMember" : "suspendMember";
    this.pageLoading = true;
    this.memberService[methodName](this.memberId).subscribe(
      (resp) => {
        this.renewStatus();
        this.pageLoading = false;
        swal(
          "Congratulations",
          "Suspend status successfully changed.",
          "success",
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public toggleWebProfileDialog(): void {
    this.showWebProfileModal = true;
  }

  public closeWebProfileDialog(): void {
    this.showWebProfileModal = false;
  }

  /** Whether the member must cancel subscriptions before deletion is available. */
  public get hasActiveSubscription(): boolean {
    return this.memberData.subscriptionStatus === 'ACTIVE_SUBSCRIPTION';
  }

  public toggleMemberDeleteDialog(): void {
    if (this.loading || this.hasActiveSubscription) {
      return;
    }
    this.showMemberDeleteModal = true;
  }

  public closeMemberDeleteDialog(): void {
    this.showMemberDeleteModal = false;
  }

  public toggleAbTestsDialog(): void {
    this.showAbTestsModal = true;
  }

  public closeAbTestsDialog(): void {
    this.showAbTestsModal = false;
  }

  public toggleDevicesDialog(): void {
    this.showDevicesModal = true;
  }

  public closeDevicesDialog(): void {
    this.showDevicesModal = false;
  }

  public toggleCancelMemberDialog(): void {
    this.showCancelMemberModal = true;
  }

  public closeCancelMemberDialog(): void {
    this.showCancelMemberModal = false;
  }

  public closeInvalidPlanDialog(): void {
    this.showInvalidPlanDialog = false;
  }

  public cancelSubscriptionEndOfPeriod(data): void {
    this.loading = true;
    this.memberService.cancelSubscriptionEndOfPeriod(this.memberId).subscribe(
      (resp) => {
        alertify.success(
          `Subscription marked for cancelling at end of the subscription period.`,
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public cancelSubscriptionNow(data): void {
    this.loading = true;
    this.memberService.cancelSubscriptionNow(this.memberId).subscribe(
      (resp) => {
        alertify.success(`Subscription is canceled successfully.`);
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public makeVip() {
    this.initialData.isVip = true;
    this.pageLoading = true;
    this.memberService.updateVipStatus(this.memberId, true).subscribe(
      (resp) => {
        this.renewStatus();
        this.memberData.isVip = true;
        this.pageLoading = false;
        swal(
          "Congratulations",
          "Successfully changed member vip status",
          "success",
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public revokeVip() {
    this.initialData.isVip = false;
    this.pageLoading = true;
    this.memberService.updateVipStatus(this.memberId, false).subscribe(
      (resp) => {
        this.renewStatus();
        this.memberData.isVip = false;
        this.pageLoading = false;
        swal(
          "Congratulations",
          "Successfully changed member vip status",
          "success",
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public addSubscription() {
    this.showSubscriptionModal = true;
  }

  public onSubscriptionAdded(data: any) {
    this.memberService.addSubscription(this.memberData.id, data).subscribe(
      () => {
        swal(
          "Congratulations",
          "The subscription was added successfully.",
          "success",
        );
        this.loadData();
      },
      (error) => {
        let message = `There was a problem adding the subscription.`;
        if (409 === error.status && "DUPLICATE_DATA" === error.error) {
          message = "A subscription with this purchase Id already exists";
        }

        alertify.error(message);
      },
    );
  }

  public onSubscriptionDialogClosed() {
    this.showSubscriptionModal = false;
  }

  get showButtons() {
    return (
      "WAITING_FOR_APPROVAL" === this.memberData.status ||
      "WAITING_FOR_PROOF" === this.memberData.status
    );
  }

  get isAccepted() {
    return "APPROVED" === this.memberData.status;
  }

  get isRejected() {
    return "REJECTED" === this.memberData.status;
  }

  get isSuspended() {
    return "SUSPENDED" === this.memberData.status;
  }

  get isValidAc() {
    return Boolean(null != this.memberData.acquisitionChannel);
  }

  private renewStatus() {
    this.memberService.getMember(this.memberData.id).subscribe(
      (resp) => {
        this.memberData.status = resp.json().status;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private alertSuccess(method): void {
    alertify.success(`Successfully ${method} member.`);
  }

  private alertError(error: any) {
    alertify.error(
      `There was a ${error.status} error with message '${error.error}'.`,
    );
  }

  public parseDatetime(str: String) {
    if (str == null || str.trim() == "") return str;

    const datetime = /\d{4}(.\d{2}){2}(\s|T)(\d{2}.){2}\d{2}/g;
    return str.replace(datetime, function replacer(match) {
      return moment(match).format("HH:mm DD MMM YYYY");
    });
  }

  get stripeBaseUrl() {
    return `https://dashboard.stripe.com/${environment.stripe_dashboard_test_mode ? 'test/' : ''}`;
  }

  get stripeCustomerLink() {
    return `${this.stripeBaseUrl}customers/${this.memberData.customerId}`;
  }

  get stripeSubscriptionLink() {
    return `${this.stripeBaseUrl}subscriptions/${this.memberData.currentSubscriptionId}`;
  }

  get referrerProfileLink() {
    return `admin/members/edit?id=${this.memberData.referrerId}`;
  }

  get billingCycleChangeTooltip() {
    const change = this.memberData && this.memberData.billingCycleChange;
    if (!change || !change.pending) {
      return '';
    }

    const effectiveAt = change.effectiveAt
      ? moment(change.effectiveAt).format('DD/MM/YYYY H:mm')
      : 'next renewal date';
    const targetCycle = this.formatBillingCycle(change.targetCycle);
    const targetDescription = targetCycle ? ` to ${targetCycle}` : '';

    return `Billing cycle change pending at next renewal date${targetDescription} (${effectiveAt}).`;
  }

  private formatBillingCycle(cycle: string) {
    if (!cycle) {
      return '';
    }

    return cycle.toLowerCase();
  }

  public refereeProfileLink(referral: Referral) {
    return `admin/members/edit?id=${referral.refereeId}`;
  }

  public refereeStripeSubscriptionLink(referral: Referral) {
    return `${this.stripeBaseUrl}subscriptions/${referral.stripeSubscriptionId}`;
  }

  public programLink(rewardProgress: RewardProgress) {
    return `admin/referral-programs/edit?id=${rewardProgress.programId}`;
  }

  public rewardLink(rewardProgress: RewardProgress) {
    return `admin/referral-rewards/edit?referralProgramId=${rewardProgress.programId}&id=${rewardProgress.rewardId}`;
  }

  public rewardInstanceLink(rewardInstance: RewardInstance) {
    return `admin/referral-rewards/edit?referralProgramId=${rewardInstance.referralProgramId}&id=${rewardInstance.referralRewardId}`;
  }

  public markReferralsEnabledFlag(checked: boolean) {
    this.memberData.referralsEnabledFlag = checked;
  }

  get employerNames(): string[] {
    return this.employers.map(emp => emp.name);
  }
}



// WEBPACK FOOTER //
// /home/runner/_work/monorepo/monorepo/admin-portal-old/src/app/member/member-edit/member-edit.component.ts